import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";
import { Modal, Button, Tooltip } from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import { getDelayedReports } from "../../../actions/reportDelay";
import ReportDelayTable, { disableReportDelay } from "./ReportDelayTable";
import UpdateDelayReportModal from "./UpdateDelayReportModal";
import { defaultDateRange, resolveReportDelayStatus } from "./utils";
import ReportDelayFilters from "./ReportDelayFilters";
import { formatAntdDateRange } from "../../../utils/helpers";
import useAbortController from "../../Common/hooks/useAbortController";
import "./ReportDelay.css";
import "./ReportDelayFormPopup.css";

const defaultRangeUnformatted = defaultDateRange("week"); // interval = week
const defaultWODateRange = defaultRangeUnformatted ? formatAntdDateRange(defaultRangeUnformatted) : null;
const source = "test_in_samples";

export default function ReportDelayFormPopup({
  open,
  onCancel,
}) {
  const [filters, setFilters] = useState(() => ({
    source,
    date_range: defaultWODateRange,
    status: "pending",
    user_id: null,
    reason: null,
    test_category_id: null,
    search: null,
  }));
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isUpdateReportModalOpen, setIsUpdateReportModalOpen] = useState(false);
  const [shouldUpdateParent, setShouldUpdateParent] = useState(false);
  const [selectedRowKeySet, setSelectedRowKeySet] = useState(() => new Set());
  const { setNewAbortController, clearAbortController, abortController } = useAbortController();

  const searchReports = async (params) => {
    const newAbortController = setNewAbortController();
    setResults([]);
    setSelectedRowKeySet(new Set());
    setLoading(true);
    try {
      const res = await getDelayedReports(params, newAbortController.signal);
      // Drop stale responses (a newer request has superseded this one)
      if (newAbortController !== abortController.current) return;

      if (res.success) {
        const rawResults = Array.isArray(res.results) ? res.results : [];
        setResults(rawResults);
        setSelectedRowKeySet(new Set());
      } else if (!res.canceled) {
        toast.error(res?.message || "Failed to fetch details");
        setResults([]);
        setSelectedRowKeySet(new Set());
      }
    } finally {
      const isLatest = newAbortController === abortController.current;
      if (isLatest) {
        setHasSearched(true);
        clearAbortController();
        setLoading(false);
      }
    }
  };

  const openReportModal = useCallback(() => {
    setIsUpdateReportModalOpen(true);
  }, []);

  const closeReportModal = useCallback((isUpdated) => {
    setIsUpdateReportModalOpen(false);
    if (!isUpdated) return;
    setShouldUpdateParent(true); // IF ANY ROW UPDATED
    const params = { source, ...(filters || {}) };
    searchReports(params);
  }, [filters, setShouldUpdateParent]); // eslint-disable-line

  const handleFiltersChange = useCallback((nextFilters) => {
    setFilters(nextFilters);
  }, []);

  const handleRowSelectionChange = useCallback((keys) => {
    setSelectedRowKeySet(new Set(keys));
  }, []);

  const selectedRowKeys = useMemo(
    () => Array.from(selectedRowKeySet),
    [selectedRowKeySet],
  );

  const selectedRows = useMemo(
    () => results.filter((row) => selectedRowKeySet.has(row.tests_in_sample_pk)),
    [results, selectedRowKeySet],
  );

  const columns = useMemo(() => ([
    {
      title: "WO ID",
      dataIndex: "workorder_id",
      key: "workorder_id",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Test",
      dataIndex: "test_name",
      key: "test_name",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Test Category",
      dataIndex: "category_name",
      key: "category_name",
      onCell: () => ({ style: { minWidth: 120 } }),
    },
    {
      title: "Result Due By",
      dataIndex: "current_eta",
      key: "current_eta",
      onCell: () => ({ style: { minWidth: 130 } }),
      render: (value) => (value ? moment(value).format("MM/DD/YYYY") : "-"),
    },
    {
      title: "Expected ETA",
      dataIndex: "expected_eta",
      key: "expected_eta",
      onCell: () => ({ style: { minWidth: 120 } }),
      render: (value, record) => {
        const updatedAt = record.override_created_at || null;
        const createdBy = record.override_created_by_name || null;
        return (
          <Tooltip
            title={(
              <div>
                {createdBy && <div>{`Reported By: ${createdBy}`}</div>}
                {updatedAt && <div>{`Reported At: ${updatedAt}`}</div>}
              </div>
            )}
            overlayClassName="ReportDelayFormPopup__ExpectedETA_Tooltip"
            placement="topLeft"
          >
            <span>{value ? moment(value).format("MM/DD/YYYY") : "-"}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      onCell: () => ({ style: { minWidth: 140 } }),
      render: (value) => value || "-",
    },
    {
      title: "Comment",
      dataIndex: "comments",
      key: "comments",
      ellipsis: true,
      onCell: () => ({ style: { minWidth: 200 } }),
      render: (value) => value || "-",
    },
    {
      title: "Test Status",
      dataIndex: "status",
      key: "status",
      onCell: () => ({ style: { minWidth: 100 } }),
      render: (value) => {
        if (!value) return "-";
        const cfg = resolveReportDelayStatus(value);
        if (!cfg) return value ?? "-";
        return <span style={{ color: cfg.style?.color }}>{cfg.label}</span>;
      },
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   onCell: () => ({ style: { width: 120 } }),
    //   render: (_, record) => (
    //     <Button size="small" type="primary" onClick={() => openReportModal(record)} disabled={...}>
    //       Report
    //     </Button>
    //   ),
    // },
  ]), []);

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setHasSearched(false);
    setSelectedRowKeySet(new Set());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const params = { source, ...(filters || {}) };
    searchReports(params);
  }, [open, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (onCancel) onCancel(shouldUpdateParent);
  };

  const handleTableRowClick = useCallback((record) => {
    const shouldDisable = disableReportDelay(record.status);
    return {
      onClick: () => {
        if (shouldDisable) return;
        setSelectedRowKeySet((prevKeys) => {
          const nextKeys = new Set(prevKeys);
          if (nextKeys.has(record.tests_in_sample_pk)) {
            nextKeys.delete(record.tests_in_sample_pk);
          } else {
            nextKeys.add(record.tests_in_sample_pk);
          }
          return nextKeys;
        });
      },
      style: { cursor: !shouldDisable ? "pointer" : "default" },
    };
  }, []);

  return (
    <>
      <Modal
        centered
        title="Report Delay"
        width={1500}
        open={open}
        onCancel={handleClose}
        rootClassName="ReportDelayFormPopup"
        footer={null}
        destroyOnClose
      >
        <div className="ReportDelayFormPopup__BodyContent">
          <div className="ReportDelayFormPopup__Section">
            <div className="ReportDelayHeaderRow">
              <ReportDelayFilters
                onFiltersChange={handleFiltersChange}
                initialFilters={{
                  search: filters.search,
                  dateRange: defaultRangeUnformatted,
                  status: filters.status,
                  selectedCategory: "",
                  selectedAssignTo: "",
                  reason: "",
                }}
                filterVisibility={{
                  reportedBy: false,
                  reason: false,
                  status: false,
                }}
                enableInitialInputFocus
              />
              <div className="ReportDelayHeaderRow__Actions">
                <Button
                  type="primary"
                  disabled={loading || selectedRowKeySet.size === 0}
                  onClick={openReportModal}
                >
                  Report
                </Button>
              </div>
            </div>

            <ReportDelayTable
              rowKey="tests_in_sample_pk"
              dataSource={results || []}
              pagination
              columns={columns}
              loading={loading}
              scroll={{ x: "max-content", y: 1 }}
              emptyText={hasSearched ? "No Data" : "Search to load data"}
              rowSelectionVisible
              selectedRowKeys={selectedRowKeys}
              onCheckboxChange={handleRowSelectionChange}
              selectionCounter={selectedRowKeySet.size}
              onRow={handleTableRowClick}
            />
          </div>
        </div>

        <div className="ReportDelayFormPopup__Footer">
          <Button onClick={handleClose}>
            Close
          </Button>
        </div>
      </Modal>

      {isUpdateReportModalOpen && (
        <UpdateDelayReportModal
          open={isUpdateReportModalOpen}
          selectedRows={selectedRows}
          onCancel={closeReportModal}
        />
      )}
    </>
  );
}
